import { IPractice } from "../modules/auth/types/auth.types";

type ClientSessionPayload = {
    clientId: string | null;
    userId: string | null;
    practice: IPractice | null;
};

enum ClientSessionMessageType {
    Update = "session-update",
    Request = "session-request",
}

type ClientSessionMessage =
    | { type: ClientSessionMessageType.Update; payload: ClientSessionPayload }
    | { type: ClientSessionMessageType.Request };

const CLIENT_ID_KEY = "clientId";
const USER_ID_KEY = "userId";
const PRACTICE_KEY = "practice";
const CHANNEL_NAME = "mantle-pp-client-context";

let channel: BroadcastChannel | null = null;
let isInitialized = false;

const canUseStorage =
    typeof window !== "undefined" &&
    typeof window.sessionStorage !== "undefined";

const canUseBroadcastChannel =
    typeof window !== "undefined" && "BroadcastChannel" in window;

function cleanupLegacyLocalStorage() {
    if (typeof window === "undefined") {
        return;
    }

    const keysToClear = [CLIENT_ID_KEY, USER_ID_KEY, PRACTICE_KEY];

    keysToClear.forEach((key) => {
        const legacyValue = window.localStorage.getItem(key);

        if (legacyValue !== null) {
            if (canUseStorage && !window.sessionStorage.getItem(key)) {
                window.sessionStorage.setItem(key, legacyValue);
            }

            window.localStorage.removeItem(key);
        }
    });
}

function readPracticeFromStorage(): IPractice | null {
    if (!canUseStorage) {
        return null;
    }

    const raw = window.sessionStorage.getItem(PRACTICE_KEY);

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw) as IPractice;
    } catch {
        return null;
    }
}

export function getClientSession(): ClientSessionPayload {
    if (!canUseStorage) {
        return {
            clientId: null,
            userId: null,
            practice: null,
        };
    }

    const clientId = window.sessionStorage.getItem(CLIENT_ID_KEY);
    const userId = window.sessionStorage.getItem(USER_ID_KEY);
    const practice = readPracticeFromStorage();

    return {
        clientId,
        userId,
        practice,
    };
}

function writeClientSession(context: ClientSessionPayload) {
    if (!canUseStorage) {
        return;
    }

    const { clientId, userId, practice } = context;

    if (clientId) {
        window.sessionStorage.setItem(CLIENT_ID_KEY, clientId);
    } else {
        window.sessionStorage.removeItem(CLIENT_ID_KEY);
    }

    if (userId) {
        window.sessionStorage.setItem(USER_ID_KEY, userId);
    } else {
        window.sessionStorage.removeItem(USER_ID_KEY);
    }

    if (practice) {
        window.sessionStorage.setItem(PRACTICE_KEY, JSON.stringify(practice));
    } else {
        window.sessionStorage.removeItem(PRACTICE_KEY);
    }
}

function ensureChannel() {
    if (!canUseBroadcastChannel) {
        return;
    }

    if (!channel) {
        channel = new BroadcastChannel(CHANNEL_NAME);

        channel.onmessage = (event: MessageEvent<ClientSessionMessage>) => {
            const message = event.data;

            if (!message) {
                return;
            }

            if (message.type === ClientSessionMessageType.Update) {
                writeClientSession(message.payload);
            } else if (message.type === ClientSessionMessageType.Request) {
                const current = getClientSession();

                if (current.clientId || current.userId || current.practice) {
                    channel?.postMessage({
                        type: ClientSessionMessageType.Update,
                        payload: current,
                    });
                }
            }
        };
    }
}

export function initClientSessionSync() {
    if (isInitialized) {
        return;
    }

    cleanupLegacyLocalStorage();

    if (!canUseBroadcastChannel) {
        isInitialized = true;
        return;
    }

    ensureChannel();
    isInitialized = true;

    channel?.postMessage({ type: ClientSessionMessageType.Request });
}

export function setClientSession(partial: {
    clientId?: string | null;
    userId?: string | null;
    practice?: IPractice | null;
}) {
    const current = getClientSession();

    const next: ClientSessionPayload = {
        clientId:
            partial.clientId !== undefined
                ? partial.clientId
                : current.clientId,
        userId: partial.userId !== undefined ? partial.userId : current.userId,
        practice:
            partial.practice !== undefined
                ? partial.practice
                : current.practice,
    };

    writeClientSession(next);

    ensureChannel();
    channel?.postMessage({
        type: ClientSessionMessageType.Update,
        payload: next,
    });
}

export function clearClientSession() {
    writeClientSession({
        clientId: null,
        userId: null,
        practice: null,
    });

    ensureChannel();
    channel?.postMessage({
        type: ClientSessionMessageType.Update,
        payload: { clientId: null, userId: null, practice: null },
    });
}
